import React, { useState, useEffect } from 'react';
import './Flashcard.css';
import { getWordKey } from '../utils/srs';
import { useLanguage } from '../contexts/LanguageContext';

const DemoFlashcard = ({ word, module, isFlipped, onFlip, onFeedback, onSRSUpdate, srsMap, setSrsMap, getDemoSRSData, updateDemoSRSData, getDemoSRSStatus }) => {
  const { t } = useLanguage();
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState('');

  const languagePrefKey = `languagePref_demo_${module.id}`;
  const [frontSideMode, setFrontSideMode] = useState(() => {
    const saved = localStorage.getItem(languagePrefKey);
    if (saved === null) return 'from';
    try {
      const parsed = JSON.parse(saved);
      if (parsed === true) return 'from';
      if (parsed === false) return 'to';
      return ['from', 'to', 'random'].includes(parsed) ? parsed : 'from';
    } catch {
      return 'from';
    }
  });
  const [randomFromOnFront, setRandomFromOnFront] = useState(() => Math.random() < 0.5);

  const cycleFrontSideMode = () => {
    const nextMode = frontSideMode === 'from' ? 'to' : frontSideMode === 'to' ? 'random' : 'from';
    if (nextMode === 'random') setRandomFromOnFront(Math.random() < 0.5);
    setFrontSideMode(nextMode);
    localStorage.setItem(languagePrefKey, JSON.stringify(nextMode));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.flashcard-gear') && !event.target.closest('.flashcard-dropdown')) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  useEffect(() => {
    if (frontSideMode === 'random') setRandomFromOnFront(Math.random() < 0.5);
  }, [word?.id, frontSideMode]);


  const key = getWordKey(word, module.id, 0);
  const srs = srsMap[key] || getDemoSRSData(key);
  const rating = getDemoSRSStatus(srs, t);

  const handleFeedback = async (knewIt) => {
    setError('');
    try {
      const updated = updateDemoSRSData(key, knewIt);

      // Update local state immediately for instant label change
      if (setSrsMap) {
        setSrsMap(prev => ({ ...prev, [key]: updated }));
      }

      if (typeof onFeedback === 'function') {
        setTimeout(() => {
          // Delay notifying the parent callback (which might be used for filtering/shuffling)
          if (onSRSUpdate) onSRSUpdate(key, updated);
          onFeedback();
        }, 400);
      } else {
        if (onSRSUpdate) onSRSUpdate(key, updated);
      }
    } catch (err) {
      setError(t('errorUpdatingProgress') || 'Failed to update your progress. Please try again.');
    }
  };

  const handleResetProgress = () => {
    const resetData = {
      learning_interval: 0,
      ease: 2.5,
      nextDue: Date.now(),
      apparentTier: 1,
      history: [],
      lastUpdated: Date.now()
    };
    localStorage.setItem(`demo_${key}`, JSON.stringify(resetData));
    if (setSrsMap) {
      setSrsMap(prev => ({ ...prev, [key]: resetData }));
    }
    if (onSRSUpdate) onSRSUpdate(key, resetData);
    setShowDropdown(false);
  };

  const handleMarkAsMastered = () => {
    const masteredData = {
      learning_interval: 10,
      ease: 2.5,
      nextDue: Date.now() + 10 * 86400000,
      apparentTier: 5,
      history: [...srs.history, { result: 1, time: Date.now() }],
      lastUpdated: Date.now()
    };
    localStorage.setItem(`demo_${key}`, JSON.stringify(masteredData));
    if (setSrsMap) {
      setSrsMap(prev => ({ ...prev, [key]: masteredData }));
    }
    if (onSRSUpdate) onSRSUpdate(key, masteredData);
    setShowDropdown(false);
  };

  // Use from_text and to_text for the new normalized structure
  const showFromOnFront = frontSideMode === 'from' || (frontSideMode === 'random' && randomFromOnFront);
  const frontContent = showFromOnFront ? word.from_text : word.to_text;
  const backContent = showFromOnFront ? word.to_text : word.from_text;
  const frontModeLabel = frontSideMode === 'from'
    ? `${t(module.from_lang)} ${t('onFront')}`
    : frontSideMode === 'to'
      ? `${t(module.to_lang)} ${t('onFront')}`
      : t('randomSideOnFront');

  return (
    <>
      <div className="flashcard-container" style={{ position: 'relative' }}>
        {error && (
          <div className="error-message" style={{ color: 'red', margin: '0.5rem 0', textAlign: 'center' }}>
            {error}
          </div>
        )}
        <div
          className={`flashcard ${isFlipped ? 'is-flipped' : ''}`}
          onClick={onFlip}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onFlip();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={isFlipped ? t('flipCardToFront') : t('flipCardToBack')}
        >
          <div className="flashcard-face flashcard-face-front">{frontContent}</div>
          <div className="flashcard-face flashcard-face-back">{backContent}</div>
        </div>

        <button
          className="flashcard-gear"
          onClick={(e) => { e.stopPropagation(); setShowDropdown(!showDropdown); }}
          aria-label={t('flashcardSettings')}
          aria-expanded={showDropdown}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M19.43 12.98c.04-.32.07-.65.07-.98s-.03-.66-.07-.98l2.11-1.65a.5.5 0 0 0 .12-.64l-2-3.46a.5.5 0 0 0-.6-.22l-2.49 1a7.03 7.03 0 0 0-1.7-.98l-.38-2.65A.488.488 0 0 0 14 2h-4a.5.5 0 0 0-.5.42l-.38 2.65c-.63.24-1.21.56-1.77.94l-2.49-1a.5.5 0 0 0-.6.22l-2 3.46a.5.5 0 0 0 .12.64l2.11 1.65c-.05.33-.08.66-.08 1s.03.67.08 1l-2.11 1.65a.5.5 0 0 0-.12.64l2 3.46c.14.24.44.33.68.22l2.49-1c.53.38 1.1.7 1.7.98l.38 2.65c.05.28.28.48.5.48h4c.28 0 .45-.2.5-.48l.38-2.65c.6-.28 1.17-.6 1.7-.98l2.49 1c.24.11.54.02.68-.22l2-3.46a.5.5 0 0 0-.12-.64l-2.11-1.65zM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7z" fill="currentColor" />
          </svg>
        </button>

        {showDropdown && (
          <div className="flashcard-dropdown" onClick={(e) => e.stopPropagation()}>
            <button className="dropdown-item front-side-mode-button" onClick={cycleFrontSideMode}>
              {frontModeLabel}
            </button>
            <button
              className="dropdown-item"
              onClick={handleResetProgress}
            >
              {t('resetProgress')}
            </button>
            <button
              className="dropdown-item"
              onClick={handleMarkAsMastered}
            >
              {t('markAsMastered')}
            </button>
            <button className="dropdown-item" onClick={() => setShowDropdown(false)}>
              {t('cancel')}
            </button>
          </div>
        )}
      </div>

      <div className="flashcard-feedback-row">
        <button
          className="flashcard-feedback-btn x"
          onClick={e => { e.stopPropagation(); handleFeedback(false); }}
          title={t('iDidntKnowIt')}
          aria-label={t('iDidntKnowIt')}
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="11" cy="11" r="11" fill="none" />
            <path d="M7 7L15 15M15 7L7 15" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
        </button>
        <button
          className="flashcard-feedback-btn check"
          onClick={e => { e.stopPropagation(); handleFeedback(true); }}
          title={t('iKnewIt')}
          aria-label={t('iKnewIt')}
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="11" cy="11" r="11" fill="none" />
            <path d="M6 12.5L10 16L16 8" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <div className="knowledge-indicator">
        <span className="knowledge-dot" style={{ background: rating.color }}></span>
        <span style={{ color: rating.color }}>{rating.label}</span>
      </div>
    </>
  );
};

export default DemoFlashcard;
